/**
 * 🎮 ARCADE ORBITALE - Station de jeux spatiale
 * Bloc animé avec effets néon et HUD futuriste
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, ChevronRight, Joystick, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const GamesZoneCard = ({ isDarkMode, onOpenGames }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #4a1d96 100%)',
        boxShadow: '0 0 50px rgba(168, 85, 247, 0.3), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(168, 85, 247, 0.4)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: ['#a855f7', '#ec4899', '#f59e0b', '#22c55e'][i % 4],
              opacity: Math.random() * 0.4 + 0.1
            }}
            animate={{ 
              opacity: [0.1, 0.6, 0.1], 
              scale: [1, 1.5, 1],
              boxShadow: ['0 0 3px currentColor', '0 0 8px currentColor', '0 0 3px currentColor']
            }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Éléments de jeu flottants */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {['🎲', '🎰', '🃏', '💎', '🎯', '🏆'].map((emoji, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl"
            style={{ left: `${10 + i * 15}%` }}
            initial={{ y: '120%', rotate: 0, opacity: 0 }}
            animate={{ 
              y: '-20%',
              rotate: [0, 360],
              opacity: [0, 0.6, 0]
            }}
            transition={{ 
              duration: 5 + i * 0.5, 
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeOut"
            }}
          >
            {emoji}
          </motion.div>
        ))}
      </div>

      {/* Scan line effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(transparent 0%, rgba(168, 85, 247, 0.03) 50%, transparent 100%)',
          backgroundSize: '100% 4px'
        }}
        animate={{ backgroundPosition: ['0% 0%', '0% 100%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      />

      {/* Bordure supérieure néon */}
      <motion.div 
        className="absolute top-0 left-0 w-full h-1"
        style={{ background: 'linear-gradient(90deg, transparent, #a855f7, #ec4899, #f59e0b, transparent)' }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-purple-500" />
      <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-pink-500" />
      <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-purple-500/50" />
      <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-pink-500/50" />

      {/* Header */}
      <div className="relative p-4 border-b border-purple-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div
              className="w-2 h-2 rounded-full bg-green-500"
              animate={{ opacity: [1, 0.5, 1], scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-green-400">SYSTÈME ACTIF</span>
          </div>
          <span className="text-[10px] font-mono text-purple-400/70">ARCADE ORBITALE v2.0</span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Icône animée style arcade */}
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 0 30px rgba(168, 85, 247, 0.4), 0 0 60px rgba(236, 72, 153, 0.2)',
                '0 0 50px rgba(168, 85, 247, 0.6), 0 0 80px rgba(236, 72, 153, 0.4)',
                '0 0 30px rgba(168, 85, 247, 0.4), 0 0 60px rgba(236, 72, 153, 0.2)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center relative"
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 50%, #f59e0b 100%)',
              border: '2px solid rgba(168, 85, 247, 0.5)'
            }}
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <Joystick className="w-12 h-12 md:w-14 md:h-14 text-white" />
            </motion.div>
            
            {/* Glow pulse */}
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ border: '2px solid rgba(168, 85, 247, 0.8)' }}
              animate={{ scale: [1, 1.1, 1], opacity: [0.8, 0, 0.8] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>

          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <h3 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2">
                <span>🎮</span> Arcade Orbitale
              </h3>
              <motion.span 
                className="px-3 py-1 rounded-full text-[10px] font-mono font-bold border"
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
                  borderColor: 'rgba(34, 197, 94, 0.5)',
                  color: '#22c55e'
                }}
                animate={{ boxShadow: ['0 0 10px rgba(34, 197, 94, 0.3)', '0 0 20px rgba(34, 197, 94, 0.5)', '0 0 10px rgba(34, 197, 94, 0.3)'] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                GRATUIT
              </motion.span>
            </div>
            
            <motion.p 
              className="text-lg font-medium mb-2"
              style={{
                background: 'linear-gradient(90deg, #f59e0b, #ec4899, #a855f7, #f59e0b)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              🎰 Jackpot • 🃏 Grattage • 🎡 Roue de la Fortune
            </motion.p>
            
            <p className="text-sm text-gray-400">
              8 mini-jeux pour remporter tampons, cadeaux et surprises ! 🎁
            </p>
          </div>

          {/* Bouton JOUER */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              onClick={onOpenGames}
              className="px-8 py-4 rounded-xl font-bold text-lg flex items-center gap-3 text-white border-0"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 50%, #f59e0b 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradient-shift 3s ease infinite',
                boxShadow: '0 0 30px rgba(168, 85, 247, 0.5), 0 10px 20px rgba(0,0,0,0.3)'
              }}
              data-testid="open-games-button"
            >
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-5 h-5" />
              </motion.span>
              JOUER
              <ChevronRight className="w-5 h-5" />
            </Button>
          </motion.div>
        </div>

        {/* Bandeau Jackpot */}
        <motion.div 
          className="mt-5 p-3 rounded-xl text-center relative overflow-hidden"
          style={{
            background: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(245, 158, 11, 0.3)'
          }}
        >
          {/* Scanner effect */}
          <motion.div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(245, 158, 11, 0.1), transparent)' }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
          
          <div className="relative flex items-center justify-center gap-2">
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="text-xl"
            >
              🏆
            </motion.span>
            <span className="text-sm font-bold text-yellow-400 font-mono">
              JACKPOT DU JOUR
            </span>
            <span className="text-sm text-gray-300">
              Carte 10€ • Bonus x2 • Surprise mystère
            </span>
          </div>
        </motion.div>
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-purple-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">🎮 ARCADE STATION v2.0</span>
        <div className="flex items-center gap-1">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-purple-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
          <motion.div className="w-1.5 h-1.5 rounded-full bg-pink-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }} />
          <motion.div className="w-1.5 h-1.5 rounded-full bg-yellow-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }} />
        </div>
      </div>
    </motion.div>
  );
};

export default GamesZoneCard;
